import { useTranslation } from 'react-i18next'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import Loading from '../../../../gateway/layout/Loading'
import DataTable from '../../../../gateway/components/DataTable'
import Authorization from '../../../../gateway/Authentication/Authorization'
import CsvWebTokenizerService from './CsvWebTokenizerService'
import DialogContext from '../../../../gateway/Authentication/DialogContext'
import { EditDialogProps } from '../../../../gateway/components/EditDialog'
import { TFunction } from 'i18next'
import { formatBooleanColumn } from '../../../../gateway/utils/DatatableUtil'
import { faFileDownload, faFileUpload, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import AuthService from '../../../../gateway/Authentication/AuthService'
import ProtectedButton from '../../../../gateway/components/ProtectedButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AppContext from '../../../../gateway/Authentication/AppContext'
import { ConfirmDialogProps } from '../../../../gateway/components/ConfirmDialog'
import { FormDataControl } from '../../../../gateway/components/FormFields'

const CsvWebTokenizer: FC<{}> = () => {
  const { t } = useTranslation()
  const csvConfig = useState({} as any)
  const [tokenizedCsvFiles, setTokenizedCsvFiles] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    async function loadData() {
      await CsvWebTokenizerService.loadTokenizedCsvFiles(setTokenizedCsvFiles)
    }

    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        dataField: 'id',
        sort: false,
        hidden: true,
      },
      {
        text: t('testdata.webtokenizer.filename'),
        dataField: 'fileName',
        sort: true,
      },
      {
        text: t('testdata.webtokenizer.method'),
        dataField: 'method',
        sort: true,
      },
      {
        text: t('testdata.webtokenizer.policy'),
        dataField: 'policy',
        sort: true,
        formatter: (cell: any, row: any) => (row.label === t('disabled') ? formatBooleanColumn(cell) : cell),
      },
      {
        text: t('testdata.webtokenizer.timestamp'),
        dataField: 'lastModified',
        sort: true,
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.adapter.view),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('testdata.webtokenizer.download')}
                onClick={() => downloadCsvFile(row)}>
                <FontAwesomeIcon icon={faFileDownload} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deleteCsvFile(t, confirmDialogPropsSetter, setTokenizedCsvFiles, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, confirmDialogPropsSetter, userPermissions])

  const defaultSorted = [
    {
      dataField: 'lastModified',
      order: 'desc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={tokenizedCsvFiles}
        columns={columns}
        caption={t('testdata.webtokenizer.header')}
        captionButtons={[
          {
            onClick: () => protectCsvFile(t, editDialogPropsSetter, setTokenizedCsvFiles, csvConfig),
            text: t('testdata.webtokenizer.protect'),
            permissions: Authorization.adapter.view,
            icon: faFileUpload,
          },
          {
            onClick: () => unveilCsvFile(t, editDialogPropsSetter, setTokenizedCsvFiles, csvConfig),
            text: t('testdata.webtokenizer.unveil'),
            permissions: Authorization.adapter.view,
            icon: faFileUpload,
          }
        ]}
        searchPlaceholder={t('testdata.webtokenizer.placeholder')}
        defaultSorted={defaultSorted}
      />
    </Loading>
  )
}

function protectCsvFile(t: TFunction, editDialogPropsSetter: any, setTokenizedCsvFiles: React.Dispatch<any>, csvConfig: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.webtokenizer.protectheader'),
    onSubmit: CsvWebTokenizerService.protectCsvFile,
    onSubmitExtraArgs: [t, setTokenizedCsvFiles],
    data: (custom: any[]) => [
      {
        id: 'csvFile',
        value: csvConfig.file,
        type: 'file',
        label: t('testdata.webtokenizer.selectfile'),
        required: true,
      },
      {
        groupLabel: t('testdata.webtokenizer.autopolicy'),
        groupName: 'autoPolicy',
        type: 'switch',
        checkboxes: [
          {
            id: 'automaticallyDeterminePolicy',
            onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e]),
            value: custom[0],
          },
        ],
      },
      togglePolicyTextField(custom[0], t),
      {
        groupLabel: t('testdata.webtokenizer.fullvalidationreq'),
        groupName: 'fullvalid',
        type: 'switch',
        checkboxes: [
          {
            id: 'fullValidationRequired',
            value: false,
          },
        ],
      },
      {
        id: 'delimiter',
        type: 'text',
        label: t('testdata.webtokenizer.delimiter'),
        placeholder: t('testdata.webtokenizer.defaultdelimiter'),
      },
      {
        id: 'dataSeparator',
        type: 'text',
        label: t('testdata.webtokenizer.dataseparator'),
        placeholder: t('testdata.webtokenizer.defaultdataseparator'),
      },
    ],
    initCustom: ['true'],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function unveilCsvFile(t: TFunction, editDialogPropsSetter: any, setTokenizedCsvFiles: React.Dispatch<any>, csvConfig: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.webtokenizer.unveilheader'),
    onSubmit: CsvWebTokenizerService.unveilCsvFile,
    onSubmitExtraArgs: [t, setTokenizedCsvFiles],
    data: (custom: any[]) => [
      {
        id: 'csvFile',
        value: csvConfig.file,
        type: 'file',
        label: t('testdata.webtokenizer.selectfile'),
        required: true,
      },
      {
        groupLabel: t('testdata.webtokenizer.autopolicy'),
        groupName: 'autoPolicy',
        type: 'switch',
        checkboxes: [
          {
            id: 'automaticallyDeterminePolicy',
            onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e]),
            value: custom[0],
          },
        ],
      },
      togglePolicyTextField(custom[0], t),
      {
        groupLabel: t('testdata.webtokenizer.fullvalidationreq'),
        groupName: 'fullvalid',
        type: 'switch',
        checkboxes: [
          {
            id: 'fullValidationRequired',
            value: false,
          },
        ],
      },
      {
        id: 'delimiter',
        type: 'text',
        label: t('testdata.webtokenizer.delimiter'),
        placeholder: t('testdata.webtokenizer.defaultdelimiter'),
      },
      {
        id: 'dataSeparator',
        type: 'text',
        label: t('testdata.webtokenizer.dataseparator'),
        placeholder: t('testdata.webtokenizer.defaultdataseparator'),
      },
    ],
    initCustom: ['true'],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function downloadCsvFile(row: any) {
  CsvWebTokenizerService.downloadCsvFile(row)
}

function deleteCsvFile(t: TFunction, confirmDialogPropsSetter: React.Dispatch<any>, setTokenizedCsvFiles: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.webtokenizer.deleteHeader'),
    body: t('testdata.webtokenizer.deleteBody', { name: row.fileName }),
    submitText: t('yes'),
    onSubmit: CsvWebTokenizerService.deleteCsvFile,
    onSubmitExtraArgs: [row, t, setTokenizedCsvFiles],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function togglePolicyTextField(custom: any, t: TFunction): FormDataControl {
  return {
    id: 'policy',
    type: 'text',
    label: t('testdata.webtokenizer.policy'),
    placeholder: t('testdata.webtokenizer.policyplaceholder'),
    required: true,
    render: !custom,
  }
}

export default CsvWebTokenizer
